/**
 * Parameters for generateDungeon(rows, columns, numDoors, numRooms, numEnemies, numTreasures).
 * Tweak the formulas below to change scaling; see table at bottom for levels 1–20.
 */
export interface DungeonParams {
  rows: number;
  columns: number;
  numDoors: number;
  numRooms: number;
  numEnemies: number;
  numTreasures: number;
}

/** Absolute limits so higher levels don't become unplayable. */
const LIMITS = {
  maxRows: 20,
  maxColumns: 19,
  maxDoors: 10,
  maxEnemies: 12,
  maxTreasures: 15,
} as const;

/**
 * Returns dungeon generator parameters for a given level.
 *
 * Rules (all level-based, easy to tweak):
 * - Size: level 1 → 6×5, level 2 → 7×6, then +1 each level, capped by LIMITS.
 * - Enemies: level 1 = 0; levels 2–3 = 1; level 4+ = 2 + (level - 4), capped.
 * - Doors: levels 1–2 = 0; level 3+ = (level - 2), capped.
 * - Rooms: always 0 (no rooms).
 * - Treasures: level 1 = 1; levels 2–3 = 2; level 4+ = 2 + (level - 3), capped.
 *
 * Example outputs (levels 1–20):
 *
 *   Level | Rows | Cols | Doors | Rooms | Enemies | Treasures
 *   ------|------|------|-------|-------|---------|----------
 *   1     | 6    | 5    | 0     | 0     | 0       | 1
 *   2     | 7    | 6    | 0     | 0     | 1       | 2
 *   3     | 8    | 7    | 1     | 0     | 1       | 2
 *   4     | 9    | 8    | 2     | 0     | 2       | 3
 *   5     | 10   | 9    | 3     | 0     | 3       | 4
 *   6     | 11   | 10   | 4     | 0     | 4       | 5
 *   7     | 12   | 11   | 5     | 0     | 5       | 6
 *   8     | 13   | 12   | 6     | 0     | 6       | 7
 *   9     | 14   | 13   | 7     | 0     | 7       | 8
 *   10    | 15   | 14   | 8     | 0     | 8       | 9
 *   11    | 16   | 15   | 9     | 0     | 9       | 10
 *   12    | 17   | 16   | 10    | 0     | 10      | 11
 *   13    | 18   | 17   | 10    | 0     | 11      | 12
 *   14    | 19   | 18   | 10    | 0     | 12      | 13
 *   15    | 20   | 19   | 10    | 0     | 12      | 14
 *   16    | 20   | 19   | 10    | 0     | 12      | 15
 *   17    | 20   | 19   | 10    | 0     | 12      | 15
 *   18    | 20   | 19   | 10    | 0     | 12      | 15
 *   19    | 20   | 19   | 10    | 0     | 12      | 15
 *   20    | 20   | 19   | 10    | 0     | 12      | 15
 */
export function getDungeonParamsForLevel(level: number): DungeonParams {
  const L = Math.max(1, Math.floor(level));

  // Size: level 1 → 6×5, level 2 → 7×6, then +1 per level, capped
  const rows = Math.min(5 + L, LIMITS.maxRows);
  const columns = Math.min(4 + L, LIMITS.maxColumns);

  // No rooms in any dungeon
  const numRooms = 0;

  // Enemies: 0 at L1; 1 at L2–3; 2 + (L - 4) at L4+, capped
  const numEnemies =
    L <= 1 ? 0 : L <= 3 ? 1 : Math.min(2 + (L - 4), LIMITS.maxEnemies);

  // Doors: 0 at L1–2; (L - 2) at L3+, capped
  const numDoors = L <= 2 ? 0 : Math.min(L - 2, LIMITS.maxDoors);

  // Treasures: 2 at L1–3; 2 + (L - 3) at L4+, capped
  const numTreasures =
    L <= 3 ? 2 : Math.min(2 + (L - 3), LIMITS.maxTreasures);

  return {
    rows,
    columns,
    numDoors,
    numRooms,
    numEnemies,
    numTreasures,
  };
}
